import { useState, useEffect } from 'react';
import { CalendarEvent, DateType, Priority, RecurringFrequency } from '../types/calendar';
import { Family } from '../store/familyStore';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import { formatDateString } from '../utils/time';
import { useToastStore } from '../store/toastStore';

interface CreateEventWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (eventData: EventFormData) => Promise<void>;
  editingEvent?: CalendarEvent | null;
  family: Family | null;
  isDarkMode: boolean;
  isProcessing?: boolean;
  initialDate?: Date | null;
}

export interface EventFormData {
  title: string;
  description?: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  memberIds: string[];
  location?: string;
  dateType: DateType;
  priority: Priority;
  reminderDays: number[];
  isRecurring: boolean;
  recurringFrequency: RecurringFrequency;
  recurringInterval: number;
  isAllDay: boolean;
}

type EventType = 'importantDate' | 'timedEvent';

export default function CreateEventWizard({
  isOpen,
  onClose,
  onSubmit,
  editingEvent,
  family,
  isDarkMode,
  isProcessing = false,
  initialDate = null,
}: CreateEventWizardProps) {
  const { isDarkMode: storeIsDarkMode } = useThemeStore();
  const { showToast } = useToastStore();
  const currentIsDarkMode = isDarkMode ?? storeIsDarkMode;
  const tokens = getDesignTokens(currentIsDarkMode);
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3>(1);
  const [eventType, setEventType] = useState<EventType | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
    startDate: formatDateString(new Date()),
    startTime: '',
    endDate: formatDateString(new Date()),
    endTime: '',
    memberIds: [],
    location: '',
    dateType: 'regular',
    priority: 'medium',
    reminderDays: [],
    isRecurring: false,
    recurringFrequency: 'weekly',
    recurringInterval: 1,
    isAllDay: false,
  });

  // Reset wizard state when modal closes or editingEvent changes
  useEffect(() => {
    if (!isOpen) {
      setWizardStep(1);
      setEventType(null);
      setShowAdvanced(false);
      // Use initialDate if provided, otherwise use today's date
      const defaultDate = initialDate || new Date();
      const dateStr = formatDateString(defaultDate);
      setFormData({
        title: '',
        description: '',
        startDate: dateStr,
        startTime: '',
        endDate: dateStr,
        endTime: '',
        memberIds: [],
        location: '',
        dateType: 'regular',
        priority: 'medium',
        reminderDays: [],
        isRecurring: false,
        recurringFrequency: 'weekly',
        recurringInterval: 1,
        isAllDay: false,
      });
    } else if (isOpen && !editingEvent) {
      // When opening the modal, set the date
      if (initialDate) {
        // When opening with initialDate (from calendar click), pre-fill the date
        const dateStr = formatDateString(initialDate);
        setFormData(prev => ({
          ...prev,
          startDate: dateStr,
          endDate: dateStr,
        }));
      } else {
        // When opening without initialDate (from Create button), use today's date
        const today = formatDateString(new Date());
        setFormData(prev => ({
          ...prev,
          startDate: today,
          endDate: today,
        }));
      }
    }
  }, [isOpen, initialDate, editingEvent]);

  // Pre-populate form when editing
  useEffect(() => {
    if (editingEvent && isOpen) {
      const startYear = editingEvent.startTime.getFullYear();
      const startMonth = (editingEvent.startTime.getMonth() + 1).toString().padStart(2, '0');
      const startDay = editingEvent.startTime.getDate().toString().padStart(2, '0');
      const startDate = `${startYear}-${startMonth}-${startDay}`;
      const startTime = `${editingEvent.startTime.getHours().toString().padStart(2, '0')}:${editingEvent.startTime.getMinutes().toString().padStart(2, '0')}`;
      
      const endYear = editingEvent.endTime.getFullYear();
      const endMonth = (editingEvent.endTime.getMonth() + 1).toString().padStart(2, '0');
      const endDay = editingEvent.endTime.getDate().toString().padStart(2, '0');
      const endDate = `${endYear}-${endMonth}-${endDay}`;
      const endTime = `${editingEvent.endTime.getHours().toString().padStart(2, '0')}:${editingEvent.endTime.getMinutes().toString().padStart(2, '0')}`;

      const memberIds = editingEvent.memberIds && editingEvent.memberIds.length > 0 
        ? editingEvent.memberIds 
        : (editingEvent.memberId ? [editingEvent.memberId] : []);

      // Determine event type based on editingEvent
      const isImportantDate = editingEvent.isImportantDate || editingEvent.dateType !== 'regular';
      // Check if times span full day (00:00 to 23:59 or similar) or if isAllDay flag is set
      const startHour = editingEvent.startTime.getHours();
      const startMin = editingEvent.startTime.getMinutes();
      const endHour = editingEvent.endTime.getHours();
      const endMin = editingEvent.endTime.getMinutes();
      const isFullDay = (startHour === 0 && startMin === 0) && (endHour === 23 && endMin === 59);
      const isAllDay = editingEvent.isAllDay === true || (isFullDay && !isImportantDate);
      const hasSpecificTime = !isFullDay && !isAllDay;
      
      // Set event type and step
      let finalStartTime = startTime;
      let finalEndTime = endTime;
      if (isImportantDate && !hasSpecificTime) {
        setEventType('importantDate');
        // Clear times for important dates
        finalStartTime = '';
        finalEndTime = '';
      } else {
        setEventType('timedEvent');
      }
      
      setFormData({
        title: editingEvent.title,
        description: editingEvent.description || '',
        startDate,
        startTime: finalStartTime,
        endDate,
        endTime: finalEndTime,
        memberIds,
        location: editingEvent.location || '',
        dateType: editingEvent.dateType || 'regular',
        priority: editingEvent.priority || 'medium',
        reminderDays: editingEvent.reminderDays || [],
        isRecurring: editingEvent.isRecurring || false,
        recurringFrequency: (typeof editingEvent.recurringPattern === 'object' && editingEvent.recurringPattern?.frequency) || 'weekly',
        recurringInterval: (typeof editingEvent.recurringPattern === 'object' && editingEvent.recurringPattern?.interval) || 1,
        isAllDay: isAllDay,
      });
      
      setWizardStep(2); // Start at step 2 when editing
    }
  }, [editingEvent, isOpen]);

  const theme = {
    bg: tokens.bg.gradient,
    card: `${tokens.card.bg} ${tokens.card.shadow} ${tokens.card.border} border`,
    header: tokens.text.primary,
    accent: tokens.button.primary,
    text: tokens.text.primary,
    textMuted: tokens.text.muted,
    border: tokens.border.light,
    button: tokens.button.outline,
  };

  const handleEventTypeSelect = (type: EventType) => {
    setEventType(type);
    // Pre-fill times for timed events
    if (type === 'timedEvent') {
      const today = new Date();
      const currentHour = today.getHours();
      const startTime = `${currentHour.toString().padStart(2, '0')}:00`;
      const endTime = `${(currentHour + 1).toString().padStart(2, '0')}:00`;
      setFormData({ ...formData, startTime, endTime });
    } else {
      // Clear times for important dates
      setFormData({ ...formData, startTime: '', endTime: '' });
    }
    setWizardStep(2);
  };

  const validateStep2 = (): boolean => {
    if (!formData.title.trim()) return false;
    if (!formData.startDate) return false;
    
    if (eventType === 'timedEvent') {
      if (!formData.endDate) return false;
      // Times are only required if not all-day
      if (!formData.isAllDay) {
        if (!formData.startTime || !formData.endTime) return false;
      }
      if (formData.memberIds.length === 0) return false;
    }
    // For important dates, memberIds are optional
    
    return true;
  };

  const handleContinue = () => {
    if (wizardStep === 2 && validateStep2()) {
      setWizardStep(3);
    }
  };

  const handleBack = () => {
    if (wizardStep === 2) {
      setWizardStep(1);
      setEventType(null);
    } else if (wizardStep === 3) {
      setWizardStep(2);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep2()) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      await onSubmit(formData);
      onClose();
    } catch (error: any) {
      showToast(error?.message || `Failed to ${editingEvent ? 'update' : 'create'} event`, 'error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className={`${theme.card} ${tokens.card.shadow} max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-3 sm:p-4 relative rounded-landing`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`${theme.header} text-xl font-bold`}>
            {editingEvent ? 'Edit Event' : 'Create New Event'}
          </h2>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className={`${theme.button} w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 min-h-[48px] min-w-[48px]`}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className={`${theme.text} text-sm font-medium`}>
              Step {wizardStep} of 3
            </span>
            <div className="flex gap-2">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`h-2 w-2 rounded-full transition-all ${
                    step <= wizardStep
                      ? isDarkMode
                        ? 'bg-gradient-to-r from-[#60a5fa] to-[#a855f7]'
                        : 'bg-gradient-to-r from-[#d9776f] to-[#c0847c]'
                      : isDarkMode
                      ? 'bg-gray-600'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 relative">
          {/* Step 1: Event Type Selection */}
          {wizardStep === 1 && (
            <div className="space-y-4">
              <h3 className={`${theme.header} text-lg font-semibold mb-4`}>
                What type of event are you creating?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleEventTypeSelect('importantDate')}
                  disabled={isProcessing}
                  className={`${theme.card} p-6 rounded-landing border-2 ${theme.border} hover:border-[var(--accent)] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 min-h-[120px] flex flex-col items-center justify-center gap-3`}
                >
                  <div className="text-4xl">📋</div>
                  <div className={`${theme.header} font-bold text-lg`}>Important Date</div>
                  <div className={`${theme.text} text-sm text-center`}>
                    Birthdays, anniversaries, holidays, milestones
                  </div>
                  <div className={`${theme.text} text-xs text-center opacity-75`}>
                    No specific time needed
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleEventTypeSelect('timedEvent')}
                  disabled={isProcessing}
                  className={`${theme.card} p-6 rounded-landing border-2 ${theme.border} hover:border-[var(--accent)] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 min-h-[120px] flex flex-col items-center justify-center gap-3`}
                >
                  <div className="text-4xl">⏰</div>
                  <div className={`${theme.header} font-bold text-lg`}>Event with Time</div>
                  <div className={`${theme.text} text-sm text-center`}>
                    Appointments, meetings, activities
                  </div>
                  <div className={`${theme.text} text-xs text-center opacity-75`}>
                    Start and end times required
                  </div>
                  <div className={`${theme.textMuted} text-xs text-center mt-1 font-semibold`}>
                    (Use this for all-day events too)
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Basic Information */}
          {wizardStep === 2 && eventType && (
            <div className="space-y-4">
              <h3 className={`${theme.header} text-lg font-semibold mb-4`}>
                Basic Information
              </h3>

              {/* Title */}
              <div>
                <label htmlFor="eventTitle" className={`block ${theme.text} text-xs font-medium mb-2`}>
                  Event Title *
                </label>
                <input
                  type="text"
                  id="eventTitle"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  disabled={isProcessing}
                  className={`w-full px-3 py-2 ${theme.button} border ${theme.border} rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:outline-none touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]`}
                  placeholder="e.g., Soccer Practice"
                />
              </div>

              {/* Date Fields - Different for each event type */}
              {eventType === 'importantDate' ? (
                <>
                  <div>
                    <label htmlFor="eventDate" className={`block ${theme.text} text-xs font-medium mb-2`}>
                      Date *
                    </label>
                    <input
                      type="date"
                      id="eventDate"
                      value={formData.startDate}
                      onChange={(e) => {
                        setFormData({ 
                          ...formData, 
                          startDate: e.target.value,
                          endDate: e.target.value // Keep end date same for important dates
                        });
                      }}
                      required
                      disabled={isProcessing}
                      className={`w-full px-3 py-2 ${theme.button} border ${theme.border} rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:outline-none touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]`}
                    />
                  </div>

                  {/* Date Type */}
                  <div>
                    <label htmlFor="dateType" className={`block ${theme.text} text-xs font-medium mb-2`}>
                      Date Type (optional)
                    </label>
                    <select
                      id="dateType"
                      value={formData.dateType}
                      onChange={(e) => {
                        const dateType = e.target.value as DateType;
                        setFormData({
                          ...formData,
                          dateType,
                          // Auto-set recurring for birthdays/anniversaries
                          isRecurring: dateType === 'birthday' || dateType === 'anniversary' || formData.isRecurring,
                          recurringFrequency: dateType === 'birthday' || dateType === 'anniversary' ? 'yearly' : formData.recurringFrequency,
                        });
                      }}
                      disabled={isProcessing}
                      className={`w-full px-3 py-2 ${theme.button} border ${theme.border} rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:outline-none touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]`}
                    >
                      <option value="regular">Regular</option>
                      <option value="birthday">Birthday</option>
                      <option value="anniversary">Anniversary</option>
                      <option value="visit">Guest Visit</option>
                      <option value="holiday">Holiday</option>
                      <option value="school">School Event</option>
                      <option value="medical">Medical Appointment</option>
                      <option value="milestone">Milestone</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  {/* Start Date & Time */}
                  <div className="grid grid-cols-2 gap-4 md:gap-2">
                    <div>
                      <label htmlFor="startDate" className={`block ${theme.text} text-xs font-medium mb-2 md:mb-1`}>
                        Start Date *
                      </label>
                      <input
                        type="date"
                        id="startDate"
                        value={formData.startDate}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          setFormData({ 
                            ...formData, 
                            startDate: newStartDate,
                            // If new start date is after end date, update end date to match start date
                            endDate: newStartDate > formData.endDate ? newStartDate : formData.endDate,
                          });
                        }}
                        required
                        disabled={isProcessing}
                        className={`w-full px-3 py-2 md:px-2 md:py-1.5 ${theme.button} border ${theme.border} rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:outline-none touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] md:min-h-[36px]`}
                      />
                    </div>
                    {!formData.isAllDay && (
                      <div>
                        <label htmlFor="startTime" className={`block ${theme.text} text-xs font-medium mb-2 md:mb-1`}>
                          Start Time *
                        </label>
                        <input
                          type="time"
                          id="startTime"
                          value={formData.startTime}
                          onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                          required
                          disabled={isProcessing}
                          className={`w-full px-3 py-2 md:px-2 md:py-1.5 ${theme.button} border ${theme.border} rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:outline-none touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] md:min-h-[36px]`}
                        />
                      </div>
                    )}
                  </div>

                  {/* End Date & Time */}
                  <div className="grid grid-cols-2 gap-4 md:gap-2">
                    <div>
                      <label htmlFor="endDate" className={`block ${theme.text} text-xs font-medium mb-2 md:mb-1`}>
                        End Date *
                      </label>
                      <input
                        type="date"
                        id="endDate"
                        value={formData.endDate}
                        onChange={(e) => {
                          const newEndDate = e.target.value;
                          setFormData({ 
                            ...formData, 
                            endDate: newEndDate,
                            // Ensure end date is not before start date
                            startDate: formData.isAllDay && newEndDate < formData.startDate ? newEndDate : formData.startDate,
                          });
                        }}
                        required
                        disabled={isProcessing}
                        min={formData.startDate}
                        className={`w-full px-3 py-2 md:px-2 md:py-1.5 ${theme.button} border ${theme.border} rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:outline-none touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] md:min-h-[36px]`}
                      />
                    </div>
                    {!formData.isAllDay && (
                      <div>
                        <label htmlFor="endTime" className={`block ${theme.text} text-xs font-medium mb-2 md:mb-1`}>
                          End Time *
                        </label>
                        <input
                          type="time"
                          id="endTime"
                          value={formData.endTime}
                          onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                          required
                          disabled={isProcessing}
                          className={`w-full px-3 py-2 md:px-2 md:py-1.5 ${theme.button} border ${theme.border} rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:outline-none touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] md:min-h-[36px]`}
                        />
                      </div>
                    )}
                  </div>

                  {/* All Day Toggle */}
                  <div className={`flex items-start gap-3 p-4 rounded-landing border-2 ${tokens.border.light} ${tokens.button.secondary}`}>
                    <input
                      type="checkbox"
                      id="isAllDay"
                      checked={formData.isAllDay}
                      onChange={(e) => {
                        const isAllDay = e.target.checked;
                        setFormData({ 
                          ...formData, 
                          isAllDay,
                          // Clear times when enabling all-day, set default times when disabling
                          startTime: isAllDay ? '' : (formData.startTime || '09:00'),
                          endTime: isAllDay ? '' : (formData.endTime || '10:00'),
                          // For all-day events, ensure end date is at least start date (but allow multi-day)
                          endDate: isAllDay && formData.endDate < formData.startDate ? formData.startDate : formData.endDate,
                        });
                      }}
                      disabled={isProcessing}
                      className="w-6 h-6 touch-target disabled:opacity-50 cursor-pointer mt-0.5"
                    />
                    <div className="flex-1">
                      <label htmlFor="isAllDay" className={`${theme.text} text-sm font-medium cursor-pointer block mb-1`}>
                        All Day Event
                      </label>
                      <p className={`${theme.textMuted} text-xs leading-relaxed`}>
                        {formData.isAllDay 
                          ? formData.startDate === formData.endDate
                            ? 'Event will span the entire day (no specific times)'
                            : `Event will span from ${formData.startDate} to ${formData.endDate} (all day)`
                          : 'Check this to make the event span entire day(s) without specific times'}
                      </p>
                    </div>
                  </div>
                </>
              )}

              {/* Family Member Selection */}
              <div>
                <label className={`block ${theme.text} text-xs font-medium mb-3`}>
                  Family Members {eventType === 'timedEvent' ? '*' : ''} {eventType === 'importantDate' && '(optional)'}
                </label>
                {eventType === 'importantDate' && (
                  <p className={`${theme.text} text-xs mb-2 opacity-75`}>
                    Family members are optional - useful for friend's birthdays, holidays, etc.
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  {family?.members.map((member) => {
                    const isSelected = formData.memberIds.includes(member.userId || '');
                    return (
                      <button
                        key={member.userId}
                        type="button"
                        onClick={() => {
                          const memberId = member.userId || '';
                          if (isSelected) {
                            setFormData({ ...formData, memberIds: formData.memberIds.filter(id => id !== memberId) });
                          } else {
                            setFormData({ ...formData, memberIds: [...formData.memberIds, memberId] });
                          }
                        }}
                        disabled={isProcessing}
                        className={`relative flex flex-col items-center gap-2 p-3 rounded-landing border-2 transition-all touch-target min-h-[80px] min-w-[80px] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 ${
                          isSelected
                            ? `${tokens.border.accent} ${tokens.button.secondary} scale-105 shadow-sm`
                            : `${theme.border} ${theme.button} hover:scale-105 active:scale-95`
                        }`}
                      >
                        <div className="relative">
                          <div
                            className="w-12 h-12 rounded-landing-full flex items-center justify-center text-white font-bold text-lg"
                            style={{ backgroundColor: member.color }}
                          >
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                          {member.avatar && (
                            <img
                              src={member.avatar}
                              alt={member.name}
                              className="absolute inset-0 w-12 h-12 rounded-landing-full object-cover border-2 z-10"
                              style={{ borderColor: member.color }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          )}
                          {isSelected && (
                            <div className={`absolute -top-1 -right-1 w-6 h-6 ${tokens.button.primary} rounded-landing-full flex items-center justify-center border-2 ${tokens.card.bg} z-20`}>
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <span className={`text-[10px] font-semibold ${isSelected ? tokens.text.accent : theme.text}`}>
                          {member.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {eventType === 'timedEvent' && formData.memberIds.length === 0 && (
                  <p className="mt-2 text-xs text-red-500">Please select at least one family member</p>
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={isProcessing}
                  className={`flex-1 ${theme.button} px-6 py-3 rounded-landing text-sm font-semibold transition-all active:scale-95 min-h-[48px] border ${theme.border} disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100`}
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={isProcessing || !validateStep2()}
                  className={`flex-1 ${theme.accent} text-white px-6 py-3 rounded-landing text-sm font-semibold transition-all active:scale-95 min-h-[48px] shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100`}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Advanced Options */}
          {wizardStep === 3 && (
            <div className="space-y-4">
              <h3 className={`${theme.header} text-lg font-semibold mb-4`}>
                Additional Details (Optional)
              </h3>

              {/* Add More Details Button */}
              {!showAdvanced && (
                <button
                  type="button"
                  onClick={() => setShowAdvanced(true)}
                  disabled={isProcessing}
                  className={`w-full ${theme.button} border ${theme.border} px-6 py-4 rounded-landing text-sm font-semibold transition-all active:scale-95 min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100`}
                >
                  + Add More Details
                </button>
              )}

              {/* Advanced Fields */}
              {showAdvanced && (
                <div className="space-y-4">
                  {/* Location */}
                  <div>
                    <label htmlFor="eventLocation" className={`block ${theme.text} text-xs font-medium mb-2`}>
                      Location (optional)
                    </label>
                    <input
                      type="text"
                      id="eventLocation"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      disabled={isProcessing}
                      className={`w-full px-3 py-2 ${theme.button} border ${theme.border} rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:outline-none touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]`}
                      placeholder="e.g., Community Center"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="eventDescription" className={`block ${theme.text} text-xs font-medium mb-2`}>
                      Description (optional)
                    </label>
                    <textarea
                      id="eventDescription"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      disabled={isProcessing}
                      className={`w-full px-3 py-2 ${theme.button} border ${theme.border} rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:outline-none touch-target resize-none disabled:opacity-50 disabled:cursor-not-allowed`}
                      placeholder="Add any additional details..."
                    />
                  </div>

                  {/* Priority */}
                  <div>
                    <label htmlFor="priority" className={`block ${theme.text} text-xs font-medium mb-2`}>
                      Priority
                    </label>
                    <select
                      id="priority"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as Priority })}
                      disabled={isProcessing}
                      className={`w-full px-3 py-2 ${theme.button} border ${theme.border} rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:outline-none touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]`}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>

                  {/* Recurring Event */}
                  <div>
                    <label className={`flex items-center gap-2 ${theme.text} text-xs font-medium mb-2`}>
                      <input
                        type="checkbox"
                        checked={formData.isRecurring || formData.dateType === 'birthday' || formData.dateType === 'anniversary'}
                        onChange={(e) => {
                          const isRecurring = e.target.checked || formData.dateType === 'birthday' || formData.dateType === 'anniversary';
                          setFormData({
                            ...formData,
                            isRecurring,
                            recurringFrequency: (formData.dateType === 'birthday' || formData.dateType === 'anniversary') ? 'yearly' : formData.recurringFrequency,
                          });
                        }}
                        disabled={isProcessing || formData.dateType === 'birthday' || formData.dateType === 'anniversary'}
                        className="w-5 h-5 touch-target disabled:opacity-50"
                      />
                      <span>Recurring Event {formData.dateType === 'birthday' || formData.dateType === 'anniversary' ? '(Auto-enabled)' : ''}</span>
                    </label>
                    {(formData.isRecurring || formData.dateType === 'birthday' || formData.dateType === 'anniversary') && (
                      <div className="ml-7 space-y-2">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="recurringFrequency" className={`block ${theme.text} text-xs font-medium mb-2`}>
                              Frequency
                            </label>
                            <select
                              id="recurringFrequency"
                              value={formData.recurringFrequency}
                              onChange={(e) => setFormData({ ...formData, recurringFrequency: e.target.value as RecurringFrequency })}
                              disabled={isProcessing || formData.dateType === 'birthday' || formData.dateType === 'anniversary'}
                              className={`w-full px-3 py-2 ${theme.button} border ${theme.border} rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:outline-none touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]`}
                            >
                              <option value="daily">Daily</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                              <option value="yearly">Yearly</option>
                            </select>
                          </div>
                          <div>
                            <label htmlFor="recurringInterval" className={`block ${theme.text} text-xs font-medium mb-2`}>
                              Occurs every {formData.recurringInterval} {formData.recurringFrequency === 'daily' ? 'day' : formData.recurringFrequency === 'weekly' ? 'week' : formData.recurringFrequency === 'monthly' ? 'month' : 'year'}
                            </label>
                            <select
                              id="recurringInterval"
                              value={formData.recurringInterval}
                              onChange={(e) => setFormData({ ...formData, recurringInterval: parseInt(e.target.value) || 1 })}
                              disabled={isProcessing}
                              className={`w-full px-3 py-2 ${theme.button} border ${theme.border} rounded-landing focus:ring-2 focus:ring-[var(--accent)] focus:outline-none touch-target disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]`}
                            >
                              {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                                <option key={num} value={num}>
                                  {num}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Reminder Days */}
                  <div>
                    <label className={`block ${theme.text} text-xs font-medium mb-2`}>
                      Reminder Days (optional)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {[7, 3, 1].map((days) => {
                        const isSelected = formData.reminderDays.includes(days);
                        return (
                          <button
                            key={days}
                            type="button"
                            onClick={() => {
                              const reminderDays = isSelected
                                ? formData.reminderDays.filter(d => d !== days)
                                : [...formData.reminderDays, days].sort((a, b) => b - a);
                              setFormData({ ...formData, reminderDays });
                            }}
                            disabled={isProcessing}
                            className={`px-3 py-2 rounded-landing text-xs font-semibold transition-all touch-target min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed ${
                              isSelected
                                ? `${tokens.button.primary} ${tokens.text.inverse} border-2 ${tokens.border.accent}`
                                : `${theme.button} border ${theme.border}`
                            }`}
                          >
                            {days} {days === 1 ? 'day' : 'days'} before
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={isProcessing}
                  className={`flex-1 ${theme.button} px-6 py-3 rounded-landing text-sm font-semibold transition-all active:scale-95 min-h-[48px] border ${theme.border} disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100`}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  className={`flex-1 ${theme.accent} text-white px-6 py-3 rounded-landing text-sm font-semibold transition-all active:scale-95 min-h-[48px] shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100`}
                >
                  {isProcessing ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{editingEvent ? 'Saving...' : 'Creating...'}</span>
                    </>
                  ) : (
                    <span>{editingEvent ? 'Save Changes' : 'Create Event'}</span>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
